// Workflow generation subsystem.
//
// The generator plans a workflow from a natural-language description (or a named template) and
// can save it as a workflow record. Authentication is by session cookie like the rest of the
// app — the API's X-Access-Token header is not available to the browser.
import * as config from "../config.js";

const readJson = async (response) => {
  if (!response) {
    return { success: false, message: "No response from the server." };
  }

  let text = "";
  try {
    text = await response.text();
  } catch (error) {
    return { success: false, message: `Could not read the response: ${error?.message}` };
  }

  let results;
  try {
    results = text ? JSON.parse(text) : {};
  } catch (error) {
    return {
      success: false,
      message: `Unexpected non-JSON response (HTTP ${response.status}): ${String(text).slice(0, 200)}`,
    };
  }

  if (response.ok === false) {
    return {
      ...results,
      success: false,
      message: results?.message || results?.detail || `Request failed with HTTP ${response.status}`,
    };
  }

  return results;
};

const getJson = async (url) => {
  let response;
  try {
    response = await fetch(url, {
      method: "GET",
      headers: { Accept: "application/json", "Content-Type": "application/json" },
      credentials: "include",
    });
  } catch (error) {
    console.log("WORKFLOW REQUEST ERROR: ", error);
    return { success: false, message: error?.message || "Request failed." };
  }

  return readJson(response);
};

/** Available generation templates, used for the template picker. */
export const getWorkflowTemplates = async () => {
  const results = await getJson(`${config.REST_SERVER_URL}/admin/workflow/templates`);

  if (results?.success === false) {
    return results;
  }

  // Tolerate a bare array or a wrapped list.
  const templates = Array.isArray(results)
    ? results
    : results?.templates ?? results?.data ?? [];

  return { ...results, success: true, templates: Array.isArray(templates) ? templates : [] };
};

/**
 * The server's registered workflow node types — what the graph builder can place.
 *
 * Returns the raw payload as well, so a debug page can show whatever the endpoint sends even
 * if its shape differs from expectations.
 */
export const getWorkflowNodes = async () => {
  const results = await getJson(`${config.REST_SERVER_URL}/admin/workflow/nodes`);

  if (results?.success === false) {
    return { ...results, success: false, payload: results };
  }

  return { success: true, payload: results };
};

/** The node catalog the planner can compose graphs from. */
export const getWorkflowCatalog = async () =>
  getJson(`${config.REST_SERVER_URL}/admin/workflow/catalog`);

/**
 * Ask the generator to build a workflow.
 *
 * @param {object} request See the endpoint's contract: kind, mode, name, description, purpose,
 *   template, options, graph, verify, verify_cycles, allow_python, max_repairs, max_expansions,
 *   dry_run, dry_run_execute, dry_run_input, save, client_id.
 * @returns {Promise<{success: boolean, saved?: object, warnings?: any[], message?: string}>}
 */
export const generateWorkflow = async (request) => {
  let response;
  try {
    response = await fetch(`${config.REST_SERVER_URL}/admin/workflow/generate`, {
      method: "POST",
      headers: { Accept: "application/json", "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify(request),
    });
  } catch (error) {
    console.log("WORKFLOW GENERATE ERROR: ", error);
    return { success: false, message: error?.message || "Request failed." };
  }

  const results = await readJson(response);

  if (results?.success === false) {
    return results;
  }

  // A successful generation may come back without an explicit success flag.
  return {
    ...results,
    success: Boolean(results?.saved || results?.graph || results?.workflow || results?.plan) || results?.success === true,
    message: results?.message,
  };
};

const parseGraph = (graph) => {
  if (!graph) return null;
  if (typeof graph === "string") {
    try {
      return JSON.parse(graph);
    } catch (error) {
      return null;
    }
  }
  return graph;
};

const looksLikeGraph = (graph) =>
  Boolean(graph) && (Array.isArray(graph.nodes) || Array.isArray(graph.edges));

/**
 * Pull the generated graph straight out of the /generate response.
 *
 * The endpoint returns the graph it built, so this is authoritative and avoids a round trip —
 * and works even when the saved record can't be read back from the workflow endpoints.
 *
 * @returns {object|null}
 */
export const extractGeneratedGraph = (results) => {
  const candidates = [
    results?.graph,
    results?.plan?.graph,
    results?.workflow?.graph,
    results?.workflow_graph?.graph,
    results?.workflow_graph,
  ];

  for (const candidate of candidates) {
    const graph = parseGraph(candidate);
    if (looksLikeGraph(graph)) {
      return graph;
    }
  }

  return null;
};

/**
 * Fetch the graph of a generated workflow so it can be loaded into an editor.
 *
 * The generator saves a workflow record, and the app has two shapes for those (the newer
 * workflow-graph record and the older workflow record), so both are tried.
 *
 * @returns {Promise<{graph: object, workflow: object}|null>}
 */
export const loadGeneratedWorkflowGraph = async (publicId) => {
  if (!publicId) return null;

  const candidates = [
    `${config.REST_SERVER_URL}/admin/workflow/workflow-graph/${publicId}`,
    `${config.REST_SERVER_URL}/admin/workflow/${publicId}`,
  ];

  for (const url of candidates) {
    const results = await getJson(url);
    if (!results || results.success === false) {
      continue;
    }

    const workflow = results.workflow ?? results.workflow_graph ?? results;
    const graph = parseGraph(workflow?.graph);

    if (graph && (Array.isArray(graph.nodes) || Array.isArray(graph.edges))) {
      return { graph, workflow };
    }
  }

  return null;
};
